const SUPPORTED_METRICS = new Set(['LCP', 'INP', 'CLS']);
// Every non-default site language is served under a /<lang>/ prefix; keep this
// list in sync with LANGUAGE_OPTIONS in src/locales/index.js so localized
// routes collapse into the same template as their English counterpart.
export const PUBLIC_LOCALE_PREFIXES = Object.freeze(['tr', 'es', 'fr', 'de', 'it', 'id', 'th', 'pt', 'hi', 'bn']);
const LOCALE_PREFIX_PATTERN = new RegExp(`^\\/(?:${PUBLIC_LOCALE_PREFIXES.join('|')})(?=\\/|$)`, 'i');

export const WEB_VITAL_THRESHOLDS = Object.freeze({
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 }
});

export const getWebVitalRating = (metric, value) => {
  const normalizedMetric = String(metric || '').trim().toUpperCase();
  const thresholds = WEB_VITAL_THRESHOLDS[normalizedMetric];
  const numericValue = Number(value);
  if (!thresholds || !Number.isFinite(numericValue)) return 'unknown';
  if (numericValue <= thresholds.good) return 'good';
  if (numericValue <= thresholds.poor) return 'needs-improvement';
  return 'poor';
};
export const getPublicRouteTemplate = (pathname = '/') => {
  const withoutLocale = String(pathname || '/').replace(LOCALE_PREFIX_PATTERN, '') || '/';
  const normalized = withoutLocale.replace(/\/+$/, '') || '/';

  if (/^\/blog\/[^/]+$/i.test(normalized)) return '/blog/:slug';
  if (/^\/vs\/[^/]+$/i.test(normalized)) return '/vs/:slug';
  if (/^\/help\/[^/]+$/i.test(normalized)) return '/help/:slug';
  if (/^\/video\/[^/]+$/i.test(normalized)) return '/video/:slug';
  if (/^\/admin(?:\/|$)/i.test(normalized)) return '/admin/:path';
  return normalized;
};

export const getDeviceClass = (viewportWidth) => {
  const width = Number(viewportWidth);
  if (!Number.isFinite(width) || width <= 0) return 'unknown';
  if (width < 768) return 'mobile';
  if (width < 1100) return 'tablet';
  return 'desktop';
};

export const buildWebVitalEvent = ({
  metric,
  value,
  pathname = '/',
  locale = 'en',
  viewportWidth = 0,
  navigationType = 'navigate',
  release = 'unknown'
} = {}) => {
  const normalizedMetric = String(metric || '').trim().toUpperCase();
  const numericValue = Number(value);
  if (!SUPPORTED_METRICS.has(normalizedMetric) || !Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return {
    metric: normalizedMetric,
    value: normalizedMetric === 'CLS'
      ? Number(numericValue.toFixed(4))
      : Math.round(numericValue),
    rating: getWebVitalRating(normalizedMetric, numericValue),
    routeTemplate: getPublicRouteTemplate(pathname),
    locale: String(locale || 'en').trim().toLowerCase().split(/[-_]/)[0] || 'en',
    deviceClass: getDeviceClass(viewportWidth),
    navigationType: String(navigationType || 'navigate').slice(0, 32),
    release: String(release || 'unknown').slice(0, 64)
  };
};

export const detectAiReferral = ({
  href = '',
  referrer = ''
} = {}) => {
  let landingUrl;
  try {
    landingUrl = new URL(href || 'https://www.stocktag.ai/');
  } catch {
    return null;
  }

  const utmSource = String(landingUrl.searchParams.get('utm_source') || '').trim().toLowerCase();
  let referrerHost = '';
  try {
    referrerHost = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    referrerHost = '';
  }

  const source = utmSource === 'chatgpt.com' || referrerHost === 'chatgpt.com'
    ? 'chatgpt.com'
    : (
      /^(?:copilot|bing-chat|bing_copilot)$/i.test(utmSource) || /(?:^|\.)copilot\.microsoft\.com$/i.test(referrerHost)
        ? 'bing-copilot'
        : (
          /^(?:perplexity|perplexity\.ai)$/i.test(utmSource) || /(?:^|\.)perplexity\.ai$/i.test(referrerHost)
            ? 'perplexity.ai'
            : ''
        )
    );

  if (!source) return null;
  return {
    source,
    landingPath: getPublicRouteTemplate(landingUrl.pathname),
    medium: String(landingUrl.searchParams.get('utm_medium') || 'referral').slice(0, 40)
  };
};

const supportsEntryType = (PerformanceObserverClass, type) => {
  const supported = PerformanceObserverClass?.supportedEntryTypes;
  return !Array.isArray(supported) || supported.includes(type);
};

export const observeWebVitals = (
  onMetric,
  {
    windowObject = typeof window !== 'undefined' ? window : null,
    documentObject = typeof document !== 'undefined' ? document : null,
    PerformanceObserverClass = typeof PerformanceObserver !== 'undefined' ? PerformanceObserver : null
  } = {}
) => {
  if (
    typeof onMetric !== 'function'
    || !windowObject
    || !documentObject
    || typeof PerformanceObserverClass !== 'function'
  ) {
    return () => {};
  }

  const observers = [];
  const emitted = new Set();
  let lcp = 0;
  let inp = 0;
  let cls = 0;
  let clsSessionValue = 0;
  let clsSessionStart = 0;
  let clsLastEntry = 0;

  const emit = (metric, value) => {
    if (emitted.has(metric)) return;
    if (!Number.isFinite(value) || value < 0) return;
    if ((metric === 'LCP' || metric === 'INP') && value === 0) return;
    emitted.add(metric);
    onMetric({ metric, value });
  };

  const observe = (type, callback, options = {}) => {
    if (!supportsEntryType(PerformanceObserverClass, type)) return;
    try {
      const observer = new PerformanceObserverClass((list) => callback(list.getEntries()));
      observer.observe({ type, buffered: true, ...options });
      observers.push(observer);
    } catch {
      // The browser may expose PerformanceObserver without a specific entry type.
    }
  };

  observe('largest-contentful-paint', (entries) => {
    const latest = entries[entries.length - 1];
    if (latest && Number.isFinite(latest.startTime)) {
      lcp = Math.max(lcp, latest.startTime);
    }
  });

  observe('layout-shift', (entries) => {
    entries.forEach((entry) => {
      if (entry.hadRecentInput || !Number.isFinite(entry.value)) return;
      const startTime = Number(entry.startTime || 0);
      const startsNewSession = (
        !clsSessionStart
        || startTime - clsLastEntry > 1000
        || startTime - clsSessionStart > 5000
      );
      if (startsNewSession) {
        clsSessionStart = startTime;
        clsSessionValue = Number(entry.value);
      } else {
        clsSessionValue += Number(entry.value);
      }
      clsLastEntry = startTime;
      cls = Math.max(cls, clsSessionValue);
    });
  });

  observe('event', (entries) => {
    entries.forEach((entry) => {
      const duration = Number(entry.duration || 0);
      if (duration > inp) inp = duration;
    });
  }, { durationThreshold: 40 });

  const flush = () => {
    emit('LCP', lcp);
    emit('INP', inp);
    emit('CLS', cls);
  };
  const onVisibilityChange = () => {
    if (documentObject.visibilityState === 'hidden') flush();
  };

  documentObject.addEventListener('visibilitychange', onVisibilityChange, true);
  windowObject.addEventListener('pagehide', flush, true);

  return () => {
    flush();
    observers.forEach((observer) => observer.disconnect());
    documentObject.removeEventListener('visibilitychange', onVisibilityChange, true);
    windowObject.removeEventListener('pagehide', flush, true);
  };
};
